import { z } from 'zod';
/**
 * TODO(#12 follow-up): deprecate `BillingTier` / `BillingStatus` in
 * `packages/shared/src/settings/billing-settings.ts` in favor of the
 * `Plan` / `SubscriptionStatus` schemas defined below. The legacy enums
 * (`['Seedling', 'Operator', 'Compound']` + `['trialing', 'active',
 * 'past_due', 'cancelled']`, note the British 'cancelled') predate the
 * Stripe wiring and shadow the schemas in this file. They're round-tripped
 * through Firestore by `apps/web/src/pages/admin-settings.tsx` and
 * `apps/functions/src/firestore/org-preferences-store.ts` as part of the
 * `BillingSettings` doc shape, so the migration touches an existing
 * Firestore field. Out-of-scope for PR #159 (Stripe hardening) because
 * the doc-shape change is too risky to bundle with the trust-boundary
 * fixes — file a follow-up issue that adds a forward-compat migration
 * (admin-settings + org-preferences-store read both legacy + new fields,
 * write only the new field) before this TODO can be cleared.
 */
/**
 * Subscription + billing schemas for the LeanOpx Stripe integration.
 *
 * Pricing is per-user / month and is mirrored against the marketing site
 * (`apps/marketing/src/components/pricing.tsx`):
 *
 *   - operator   — $99.99 / seat / month · solo founders, 1-seat default
 *   - team       — $149.99 / seat / month · featured, "most popular"
 *   - enterprise — custom-quote, no self-serve checkout
 *
 * The `OrgSubscription` doc lives at the top-level
 * `subscriptions/{orgId}` path (one per org — `orgId` is the doc id, not
 * a subcollection key). Stripe is the source of truth for status + period
 * bounds; we mirror just enough to gate org access without round-tripping
 * to Stripe on every request.
 * Webhook handlers in `apps/functions/src/http/router.ts` (inline branch
 * on `/billing/stripe/webhook`) refresh this doc on three Stripe events:
 * `checkout.session.completed` (initial trial→active flip), and
 * `customer.subscription.{updated,deleted}` (renewals / plan changes /
 * cancellation). All other event types fall through to a 200 OK no-op.
 *
 * Note: this is the *per-org billing mirror*. The graph node for Stripe
 * subscriptions read out of customers' own Stripe connectors lives at
 * `graph/work-artifacts.ts` as `Subscription` (different domain — that
 * one is data about *their* customers' subs, not about *their* sub to us).
 */
/**
 * Marketing-aligned plan ids. `enterprise` is a custom-quote tier — it
 * exists in this enum so the `OrgSubscription` doc can record an
 * enterprise deal, but `CheckoutSessionRequest.plan` deliberately excludes
 * it (sales provisions enterprise subs out-of-band).
 */
export const PLANS = ['operator', 'team', 'enterprise'];
export const Plan = z.enum(PLANS);
/**
 * Plans the self-serve flow can sell through Stripe Checkout. Excludes
 * `enterprise` (sales-led, custom-quote — no Stripe Price ID, contact
 * sales via mailto). The runtime guard for the Checkout endpoint MUST
 * parse Stripe metadata.plan through this enum rather than a bare cast.
 */
export const SELF_SERVE_PLANS = ['operator', 'team'];
export const SelfServePlan = z.enum(SELF_SERVE_PLANS);
/**
 * Mirrors the Stripe `Subscription.status` surface 1:1. We don't collapse
 * any of these — `past_due` vs `incomplete` vs `incomplete_expired` each
 * gate different UX (dunning banner vs. setup-blocked vs. hard-paywall).
 *
 *   - trialing            — inside the 14-day trial, no payment method needed
 *   - active              — paid + current
 *   - past_due            — invoice failed, Stripe is retrying (grace period)
 *   - canceled            — explicitly canceled, access continues until period end
 *   - incomplete          — first payment failed, awaiting payment method update
 *   - incomplete_expired  — incomplete sub that timed out (Stripe auto-cleans)
 */
export const SUBSCRIPTION_STATUSES = [
    'trialing',
    'active',
    'past_due',
    'canceled',
    'incomplete',
    'incomplete_expired',
];
export const SubscriptionStatus = z.enum(SUBSCRIPTION_STATUSES);
/**
 * Per-org subscription mirror. Lives at the top-level
 * `subscriptions/{orgId}` path (one doc per org, `orgId` is the doc id).
 *
 * Nullability notes:
 *   - `plan` is null while an org is in trial without having selected a paid
 *     plan yet (e.g. fresh sign-up, status='trialing', no checkout completed).
 *   - `stripeCustomerId` / `stripeSubscriptionId` are null until the first
 *     checkout completes — orgs created via admin-provisioning won't have a
 *     Stripe customer until someone hits the billing portal.
 *   - `currentPeriodStart` / `currentPeriodEnd` are null for status='trialing'
 *     because Stripe only mints period bounds once a paid subscription kicks in.
 *   - `trialEndsAt` is null after the trial ends (we don't backfill historical
 *     trial bounds).
 */
export const OrgSubscription = z.object({
    orgId: z.string().min(1),
    /** `null` while still in trial without a paid plan picked yet. */
    plan: Plan.nullable(),
    status: SubscriptionStatus,
    stripeCustomerId: z.string().nullable(),
    stripeSubscriptionId: z.string().nullable(),
    seats: z.number().int().min(1).default(1),
    /** ISO datetime; null while trialing (Stripe only mints periods on paid subs). */
    currentPeriodStart: z.string().nullable(),
    /** ISO datetime; null while trialing (see above). */
    currentPeriodEnd: z.string().nullable(),
    /** ISO datetime; null once the trial has ended (not backfilled). */
    trialEndsAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Request body for `POST /api/billing/checkout` — kicks off a Stripe
 * Checkout session for a self-serve plan. Enterprise is excluded here
 * because that tier is custom-quote (sales-led, no checkout url).
 */
export const CheckoutSessionRequest = z.object({
    plan: SelfServePlan,
    seats: z.number().int().min(1).default(1),
    successUrl: z.string().url(),
    cancelUrl: z.string().url(),
});
/** Response from `POST /api/billing/checkout` — Stripe-hosted Checkout URL. */
export const CheckoutSessionResponse = z.object({
    url: z.string().url(),
});
/**
 * Response from `POST /api/billing/portal` — Stripe-hosted Billing Portal
 * URL (where the operator updates payment method, downloads invoices,
 * cancels, etc.).
 */
export const BillingPortalLinkResponse = z.object({
    url: z.string().url(),
});
